import { createAsyncThunk, AnyAction } from "@reduxjs/toolkit"
import { ThunkDispatch } from "redux-thunk"

import i18n from "config/i18n"
import { API } from "redux/api"
import {
  SubscriptionValues,
  SubscriptionUpgradeValues,
  SubscriptionUpdateValues,
} from "types"
import {
  start as startPolling,
  reset as resetPolling,
} from "redux/features/polling/pollingSlice"
import {
  POLLING_INTERVAL,
  POLLING_TIMEOUT,
  SUBSCRIPTION_STATE_ACTIVE,
  SUBSCRIPTION_STATE_FAILED,
} from "config"

export const createSubscription = createAsyncThunk(
  "subscription-session/create",
  async (values: SubscriptionValues) => {
    const response = await API.post("/subscriptions", values)
    return response.data
  },
)

export const upgradeSubscription = createAsyncThunk(
  "subscription-session/upgrade",
  async (values: SubscriptionUpgradeValues) => {
    const response = await API.post("/subscriptions/upgrade", values)
    return response.data
  },
)

export const fetchSubscriptions = createAsyncThunk(
  "subscriptions/fetch",
  async () => {
    const response = await API.get("/subscriptions")
    return response.data.subscriptions
  },
)

export const fetchSubscription = createAsyncThunk(
  "subscription/fetch",
  async (subscription_id: string) => {
    const response = await API.get(`/subscriptions/${subscription_id}`)
    return response.data.subscription
  },
)

export const updateSubscription = createAsyncThunk(
  "subscription/update",
  async (values: SubscriptionUpdateValues) => {
    const response = await API.patch(
      `/subscriptions/${values.subscription_id}`,
      values,
    )
    return response.data
  },
)

export const cancelSubscription = createAsyncThunk(
  "subscription/cancel",
  async (subscription_id: string) => {
    const response = await API.post(
      `/subscriptions/${subscription_id}/cancel`,
      {
        subscription_id,
      },
    )
    return response.data
  },
)

export const makeSubscription = (values: SubscriptionValues) => {
  return function (dispatch: ThunkDispatch<unknown, unknown, AnyAction>) {
    return new Promise<{
      id: string
    }>((resolve, reject) => {
      return dispatch(createSubscription(values))
        .then(
          ({ payload }) => {
            if (!payload || !payload.subscription) {
              return reject(payload.error)
            }

            const { id } = payload.subscription

            resolve({ id })
          },
          () => {
            reject(i18n.t("notice.subscription.session.failure"))
          },
        )
        .catch(() => {
          reject(i18n.t("notice.subscription.session.failure"))
        })
    })
  }
}

export const makeUpgradeSubscription = (values: SubscriptionUpgradeValues) => {
  return function (dispatch: ThunkDispatch<unknown, unknown, AnyAction>) {
    return new Promise<{
      id: string
    }>((resolve, reject) => {
      return dispatch(upgradeSubscription(values))
        .then(
          ({ payload }) => {
            if (!payload || !payload.subscription) {
              return reject(payload.error)
            }

            const { id } = payload.subscription

            resolve({ id })
          },
          () => {
            reject(i18n.t("notice.subscription.session.failure"))
          },
        )
        .catch(() => {
          reject(i18n.t("notice.subscription.session.failure"))
        })
    })
  }
}

export const makeUpdateSubscription = (values: SubscriptionUpdateValues) => {
  return function (dispatch: ThunkDispatch<unknown, unknown, AnyAction>) {
    return new Promise((resolve, reject) => {
      return dispatch(updateSubscription(values))
        .then(
          ({ payload }) => {
            if (!payload) {
              return reject(i18n.t("notice.subscription.session.unavailable"))
            }

            const timerId = setInterval(async () => {
              const { subscription_id } = values

              const result = await dispatch(fetchSubscription(subscription_id))
              const { payload } = result
              const { state } = payload

              if (state === SUBSCRIPTION_STATE_ACTIVE) {
                clearInterval(timerId)
                dispatch(resetPolling())

                resolve(subscription_id)
              } else if (state === SUBSCRIPTION_STATE_FAILED) {
                clearInterval(timerId)
                dispatch(resetPolling())

                reject(i18n.t("notice.subscription.update.failure"))
              }
            }, POLLING_INTERVAL)

            dispatch(startPolling({ pollingId: timerId }))

            setTimeout((subscription_id: string) => {
              clearInterval(timerId)
              dispatch(resetPolling())
              if (!subscription_id) {
                reject(i18n.t("notice.subscription.update.failure"))
              }
            }, POLLING_TIMEOUT)
          },
          () => {
            reject(i18n.t("notice.subscription.session.failure"))
          },
        )
        .catch((error) => {
          reject(error)
        })
    })
  }
}

export const makeCancelSubscription = (subscription_id: string) => {
  return function (dispatch: ThunkDispatch<unknown, unknown, AnyAction>) {
    return new Promise((resolve, reject) => {
      return dispatch(cancelSubscription(subscription_id))
        .then(
          ({ payload }) => {
            if (!payload || !payload.subscription) {
              return reject(payload.error)
            }

            const { id } = payload.subscription

            // NOTE: update subscription card
            dispatch(fetchSubscription(subscription_id))

            resolve(id)
          },
          () => {
            reject(i18n.t("notice.subscription.session.failure"))
          },
        )
        .catch((error) => {
          reject(error)
        })
    })
  }
}
